import { useUserStore } from '@/stores'
import { ElMessage } from 'element-plus'
import router from './index'
import { registerDynamicRoutes, clearDynamicRoutes, isRoutesLoaded, registerNotLogRoutes, clearNotLogRoutes } from './index'

// 不需要登录的页面路径（白名单）
const whiteList = [
    '/',
    '/home',
    '/login',
    '/register',
    '/forgot-password',
    '/404',
    '/403',
    '/auth',
    '/cloudApi',
    '/videoApi',
    '/about',
    '/openInfo',
    '/app',
    '/deploy',
    '/support',
    '/cloud',
    '/privacy',
    '/terms'
]

// 个人中心相关路由前缀
const personalCenterPrefix = '/personal-center'

// 应用管理相关路由（需要clientSecret权限）
const appManageRoutes = [
    '/personal-center/app-manage',
    '/personal-center/app-manage/'
]

// 调试模式开关
const DEBUG_MODE = process.env.NODE_ENV === 'development'

/**
 * 路由前置守卫
 * @param {Object} to 即将要进入的目标路由对象
 * @param {Object} from 当前导航正要离开的路由
 * @param {Function} next 进行管道中的下一个钩子
 */
export function beforeEachGuard(to, from, next) {
    // 如果路由还没加载完成，等待加载完成后再继续
    if (!isRoutesLoaded()) {
        // 使用setTimeout避免阻塞，等待路由加载完成
        const checkRoutes = () => {
            if (isRoutesLoaded()) {
                next(to.fullPath) // 重新导航到目标路由
            } else {
                setTimeout(checkRoutes, 50) // 50ms后再次检查
            }
        }
        checkRoutes()
        return
    }

    const userStore = useUserStore()
    const { isLoggedIn, token } = userStore

    // 特殊处理：个人中心相关路由权限校验
    if (to.path.startsWith(personalCenterPrefix) || to.path === '/profile') {
        handlePersonalCenterRoute(to, from, next, userStore)
        return
    }

    // 特殊处理：/profile页面需要额外校验
    if (to.path === '/profile') {
        handleProfileRoute(to, from, next, userStore)
        return
    }

    // 特殊处理：/personal-center/audit-manage路由
    if (to.path.startsWith('/personal-center/audit-manage')) {
        if (isLoggedIn) {
            const userRoles = userStore.getRoles
            const hasRole1 = userRoles.some(role => role.id == 1)
            
            if (hasRole1) {
                // 角色为1的已登录用户访问此路由，直接清空登录状态并跳转到auth
                clearDynamicRoutes()
                registerNotLogRoutes()
                userStore.clearUser()
                next({ path: '/auth' })
                return
            }
        }
    }

    // 检查路由是否需要认证（优先使用路由meta配置，其次检查白名单）
    const requiresAuth = to.meta?.requiresAuth === false ? false :
        (!whiteList.some(path => to.path.startsWith(path)))

    // 已登录用户访问登录相关页面，重定向到首页
    if (isLoggedIn && isLoginPath(to.path)) {
        next({ path: '/' })
        return
    }

    // 页面不需要认证，直接放行
    if (!requiresAuth) {
        next()
        return
    }

    // 对于需要认证的路由，如果没有token，直接重定向到登录页，不进行后续异步操作
    if (!token) {
        ElMessage.warning('请先登录')
        next('/auth')
        return
    }

    // 处理需要认证的页面（已确保有token）
    handleAuthRequiredRoute(to, from, next, userStore, token)
        .catch(error => {
            console.error('路由守卫处理异常:', error)
            // 发生异常时重定向到首页
            ElMessage.error('页面加载失败，请刷新重试')
            next({ path: '/' })
        })
}

/**
 * 处理个人中心相关路由权限校验
 */
function handlePersonalCenterRoute(to, from, next, userStore) {
    const { isLoggedIn, token } = userStore
    
    // 调试信息输出
    if (DEBUG_MODE) {
        console.log('[路由守卫] 处理个人中心路由:', to.path)
        console.log('[路由守卫] 用户登录状态:', isLoggedIn)
        console.log('[路由守卫] Token存在:', !!token)
    }

    // 1. 未登录用户处理
    if (!isLoggedIn || !token) {
        if (DEBUG_MODE) {
            console.log('[路由守卫] 未登录用户访问个人中心，重定向到登录页')
        }
        
        // ElMessage.warning('请先登录以访问个人中心')
        
        // 重定向到登录页，并保留原始目标路径
        next({
            path: '/auth',
            query: { 
                redirect: to.fullPath,
                from: 'personal-center'
            }
        })
        return
    }

    // 2. 已登录用户处理 - 检查应用管理权限
    if (isAppManageRoute(to.path)) {
        handleAppManageRoute(to, from, next, userStore)
        return
    }

    // 3. 检查用户角色，如果是审核员（角色ID为2），重定向到审核管理页面
    const userRoles = userStore.getRoles
    const hasRole2 = userRoles.some(role => role.id == 2)
    
    if (hasRole2 && to.path === '/personal-center') {
        // 审核员访问个人中心首页时，重定向到审核管理页面
        next('/personal-center/audit-manage?auditStatus=0&pageNum=1&pageSize=10')
        return
    }

    // 4. 正常访问个人中心其他页面
    next()
}

/**
 * 判断是否为应用管理路由
 */
function isAppManageRoute(path) {
    return appManageRoutes.some(route => path.startsWith(route))
}

/**
 * 处理应用管理路由权限校验
 */
function handleAppManageRoute(to, from, next, userStore) {
    const hasClientId = !!userStore.getClientId
    
    if (DEBUG_MODE) {
        console.log('[路由守卫] 检查应用管理权限，clientId存在:', hasClientId)
    }

    // 缺少clientId的用户按角色重定向
    if (!hasClientId) {
        if (DEBUG_MODE) {
            console.log('[路由守卫] 缺少clientSecret，按角色重定向')
        }

        const userRoles = userStore.getRoles
        const isAuditor = Array.isArray(userRoles) && userRoles.some(role => role.id == 2)

        if (isAuditor) {
            next('/personal-center/audit-manage?auditStatus=0&pageNum=1&pageSize=10')
        } else {
            next('/personal-center/info-manage')
        }
        return
    }

    // 有权限的用户正常访问
    next()
}

/**
 * 处理个人中心路由
 */
function handleProfileRoute(to, from, next, userStore) {
    const { isLoggedIn, getRoles, userInfo } = userStore
    
    // 未登录用户不能访问/profile页面，重定向到404
    if (!isLoggedIn) {
        next({ path: '/login' })
        return
    }
    
    // 对于已登录用户，检查角色和applyed状态
    const hasRole1 = getRoles.some(role => role.id == 1)
    const isApplyedFalse = userInfo?.applyed === false
    
    // 只有角色为1且applyed为false的用户才能访问/profile页面
    if (hasRole1 && isApplyedFalse) {
        next()
    } else {
        // 不符合条件的用户重定向到404
        next({ path: '/404' })
    }
}

/**
 * 判断是否为登录相关路径
 */
function isLoginPath(path) {
    return path.startsWith('/login') || 
           path.startsWith('/register') || 
           path === '/auth'
}

/**
 * 处理需要认证的路由
 */
async function handleAuthRequiredRoute(to, from, next, userStore, token) {
    try {
        // 统一验证：无论角色，先验证token有效性并获取最新用户信息
        const result = await userStore.getUserInfoFromServer()
        if (!result.success) {
            // 获取用户信息失败，清除登录状态重新登录
            clearDynamicRoutes()
            registerNotLogRoutes()
            userStore.clearUser()
            // ElMessage.warning('登录状态已过期或无效，请重新登录')
            next({ path: '/auth' })
            return
        }

        // 用户信息存在，清除未登录路由并注册动态路由
        clearNotLogRoutes()
        const dynamicRoutes = userStore.getDynamicRoutes
        if (dynamicRoutes && dynamicRoutes.length > 0) {
            registerDynamicRoutes(dynamicRoutes)
        }

        // 检查当前路由权限
        if (to.meta?.perms && !userStore.hasPermission(to.meta.perms)) {
            ElMessage.error('您没有访问该页面的权限')
            next({ path: '/' })
            return
        }

        // 特殊处理：无clientId时禁止访问应用管理页面
        const isAppManagePage = to.path.includes('app-manage')
        const hasClientId = !!userStore.getClientId
        if (isAppManagePage && !hasClientId) {
            // ElMessage.warning('您暂无应用管理权限')
            
            // 根据用户角色重定向到合适的页面
            const userRoles = userStore.getRoles
            const userRoleId = userRoles.length > 0 ? userRoles[0].id : null
            
            if (userRoleId == 2) {
                next('/personal-center/audit-manage?auditStatus=0&pageNum=1&pageSize=10')
            } else if (userRoleId == 1) {
                next('/personal-center/info-manage')
            } else {
                next('/personal-center')
            }
            return
        }

        next()
    } catch (error) {
        console.error('验证用户信息时发生错误:', error)
        // 发生异常时重定向到登录页
        clearDynamicRoutes()
        registerNotLogRoutes()
        userStore.clearUser()
        ElMessage.error('验证用户状态失败，请重新登录')
        next({ path: '/auth' })
    }
}

/**
 * 路由后置守卫
 * @param {Object} to 即将要进入的目标路由对象
 * @param {Object} from 当前导航正要离开的路由
 */
export function afterEachGuard(to, from) {
    // 设置页面标题
    document.title = to.meta?.title ? 
        `${to.meta.title} - 无人车开放平台` : 
        '无人车开放平台'

    // 页面切换完成后的处理
    // 可以在这里添加页面访问统计、埋点等逻辑
}

/**
 * 路由错误处理
 * @param {Error} error 路由错误对象
 */
export function onErrorGuard(error) {
    console.error('路由错误:', error)
    
    // 根据不同的错误类型提供更具体的提示和处理
    if (error.type === 'NavigationDuplicated') {
        // 导航重复（通常不需要处理，是正常现象）
        console.warn('检测到重复导航操作，已自动忽略:', error)
        return // 不显示错误提示
    }
    
    // 处理各种错误类型
    handleErrorByType(error)
}

/**
 * 根据错误类型处理路由错误
 */
function handleErrorByType(error) {
    if (error.message?.includes('Failed to resolve module specifier')) {
        // 组件路径解析错误
        console.error('组件路径解析错误，可能是组件路径错误、文件不存在或导入失败:', error)
    } else if (error.message?.includes('Failed to fetch dynamically imported module')) {
        // 组件异步加载失败
        console.error('组件异步加载失败，可能是网络问题或资源不存在:', error)
    } else if (error.code === 404) {
        // 404错误
        console.warn('页面不存在:', error)
        // 重定向到404页面
        router.push('/404')
    } else if (error.code === 403) {
        // 403权限错误
        console.warn('权限验证错误:', error)
        // 重定向到首页
        router.push('/')
    } else if (error.message?.includes('params') || error.message?.includes('query')) {
        // 路由参数解析错误
        console.error('路由参数解析错误:', error)
    } else {
        // 其他未分类的错误
        console.error('未分类的路由错误:', error)
    }
}
